import axios from "axios";

// Determine if backend is running in HTTPS mode based on environment or check
const isHttpsBackend =
  process.env.REACT_APP_BACKEND_HTTPS === "true" ||
  (typeof window !== "undefined" && window.location.protocol === "https:");

// In production, REACT_APP_API_URL points to the backend (e.g. https://api.rmultiverse.com)
// In development, relative '/api' is proxied to the backend via setupProxy.js
const API_BASE = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/api`
  : '/api';

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: 30000, // 30 second timeout
  // Handle SSL certificate issues in development
  onUploadProgress: (progressEvent) => {
    // Handle progress if needed
  },
  onDownloadProgress: (progressEvent) => {
    // Handle progress if needed
  },
});

// In development, for self-signed certificates, we'll handle errors gracefully
// The browser will show security warnings, but the requests should work
if (isHttpsBackend) {
  console.warn(
    "🔐 HTTPS Backend detected. You may need to accept security warnings in the browser."
  );
}

// Add request interceptor to log requests and handle authentication
api.interceptors.request.use(
  (config) => {
    console.log("API Request:", config.method?.toUpperCase(), config.url);
    const token = localStorage.getItem("token");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error("Request error:", error);
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    console.error("API Error:", error);
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't attempt to refresh token if the error came from the login endpoint itself
      if (originalRequest.url.includes("/auth/login")) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;
      try {
        const { data } = await axios.post(
          `${API_BASE}/auth/refresh-token`
        );
        localStorage.setItem("token", data.token);
        axios.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;
        return api(originalRequest);
      } catch (err) {
        // Handle refresh token failure (e.g., redirect to login)
        console.error("Session expired. Please log in again.");
        localStorage.removeItem("token");
        
        // Only redirect if we're not already on the login page
        if (!window.location.pathname.includes("/login")) {
          window.location.href = "/login";
        }
      }
    }

    return Promise.reject(error);
  }
);

export default api;
